import { useSessionValue } from '@portal/appLayer/auth';
import { Metafield } from '@portal/gql/shopifyStorefront/graphql';
import { useGateValue } from '@statsig/react-bindings';
import { useCallback, useMemo } from 'react';
import { SSGates } from '../context/StatsigContext/StatsigFlags';

interface AgzAccount {
  isAgzAccount: boolean;
  hasReceivedAgz: boolean;
  isGrandfatheredAgz: boolean;
}
interface UseAgzAccountProps extends AgzAccount {
  isAgzGateEnabled: boolean;
  recheckSession: () => AgzAccount;
}

const AGZ_PHASE_1_TAG = 'AGZ Phase 1';

/**
 * Hook to determine AGZ status
 * @returns {UseAgzAccountProps}
 * @property {boolean} isAgzAccount       - Auth'd users only - indicates if the user is eligible for AGZ
 * @property {boolean} hasReceivedAgz     - Auth'd users only - indicates if the user has received the AGZ welcome kit
 * @property {boolean} isGrandfatheredAgz - Auth'd users only - indicates grandfathered status based on AGZ Phase 1 tag
 * @property {boolean} isAgzGateEnabled   - Indicates AGZ gate status regardless of auth status
 * @property {function} recheckSession    - Function to re-evaluate AGZ account status
 */
export function useAGZAccount(): UseAgzAccountProps {
  const session = useSessionValue();
  const isAgzGateEnabled = useGateValue(SSGates.enableAGZ);

  const recheckSession = useCallback((): AgzAccount => {
    let isAgzAccount = false;
    let hasReceivedAgz = false;
    let isGrandfatheredAgz = false;

    if (session?.customer) {
      const { customer } = session;

      // AGZ Phase 1 tag determines grandfathered pricing
      const customerHasAGZTag = customer.tags?.includes(AGZ_PHASE_1_TAG) ?? false;
      isGrandfatheredAgz = customerHasAGZTag;

      // TODO - remove customerHasAGZTag condition after full launch
      // Keep for now to handle existing AGZ P1 customers who may fall outside the US/CA launch gate
      isAgzAccount = customerHasAGZTag || isAgzGateEnabled;

      const metafield = customer.metafields?.find(
        (mf: Metafield) => mf.key === 'agz_welcome_box_received'
      );

      hasReceivedAgz = metafield?.value === true || metafield?.value === 'true';
    }
    return { isAgzAccount, hasReceivedAgz, isGrandfatheredAgz };
  }, [session, isAgzGateEnabled]);

  const { isAgzAccount, hasReceivedAgz, isGrandfatheredAgz } = useMemo(
    () => recheckSession(),
    [recheckSession]
  );

  return { isAgzAccount, hasReceivedAgz, isGrandfatheredAgz, isAgzGateEnabled, recheckSession };
}
